/**
 * Budget Tracker Page - Auxilio Finance
 * Track income and expenses with Quicken QIF import/export support
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PlusIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  FunnelIcon,
  ArrowUpTrayIcon,
  SparklesIcon,
  ChartBarIcon,
  TrashIcon,
  PencilIcon,
  XMarkIcon,
  CheckIcon,
  BanknotesIcon,
  CalendarIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import apiClient from '../services/api';
import { Button } from '../components/ui/Button';
import LoadingOverlay from '../components/ui/LoadingOverlay';
import { ExportDropdownMenu } from '../components/ui/ExportDropdownMenu';
import { useThemeStore } from '../store/themeStore';

interface Transaction {
  id: string;
  type: 'income' | 'expense' | 'transfer';
  category_id: string;
  amount: number;
  description?: string;
  date: string;
  is_recurring: boolean;
  recurrence_period?: string;
  tags: string[];
  created_at: string;
}

interface Category {
  id: string;
  name: string;
  icon: string;
  group?: string;
  quicken_category: string;
}

interface BudgetSummary {
  month: string;
  total_income: number;
  total_expenses: number;
  net: number;
  savings_rate: number;
  income_by_category: Record<string, number>;
  expense_by_category: Record<string, number>;
  transaction_count: number;
}

interface AIInsights {
  insights: string[];
  recommendations: string[];
  areas_of_concern: string[];
  positive_notes: string[];
  savings_opportunities: string[];
}

const EXPENSE_COLORS = [
  '#f59e0b', '#ef4444', '#10b981', '#3b82f6', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
];

export const BudgetTracker: React.FC = () => {
  const { t } = useTranslation();
  const theme = useThemeStore((state) => state.theme);

  // Determine if we're in dark mode for date input styling
  const isDarkMode = theme === 'dark' ||
    (theme === 'system' && typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const dateInputStyle: React.CSSProperties = { colorScheme: isDarkMode ? 'dark' : 'light' };

  // State
  const [activeTab, setActiveTab] = useState<'overview' | 'transactions' | 'budget' | 'insights'>('overview');
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [incomeCategories, setIncomeCategories] = useState<Category[]>([]);
  const [expenseCategories, setExpenseCategories] = useState<Category[]>([]);
  const [summary, setSummary] = useState<BudgetSummary | null>(null);
  const [trends, setTrends] = useState<any[]>([]);
  const [insights, setInsights] = useState<AIInsights | null>(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(false);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingOverlay, setLoadingOverlay] = useState<{ isVisible: boolean; message: string; subMessage?: string }>({
    isVisible: false,
    message: '',
    subMessage: undefined
  });

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [transactionForm, setTransactionForm] = useState({
    type: 'expense' as 'income' | 'expense',
    category_id: '',
    amount: '',
    description: '',
    date: new Date().toISOString().slice(0, 10),
    is_recurring: false,
    recurrence_period: '',
    tags: [] as string[],
  });

  // Filter state
  const [filterType, setFilterType] = useState<'all' | 'income' | 'expense'>('all');
  const [filterCategory, setFilterCategory] = useState('');

  // Trend period state
  const [trendPeriod, setTrendPeriod] = useState<'this_month' | 'six_months' | 'one_year' | 'ytd'>('six_months');

  // Expense breakdown sort state
  const [expenseSort, setExpenseSort] = useState<'amount_desc' | 'amount_asc' | 'percent_desc' | 'percent_asc'>('amount_desc');

  // Budget vs Actual state
  const [budgetVsActualData, setBudgetVsActualData] = useState<any>(null);
  const [budgetViewBy, setBudgetViewBy] = useState<'category' | 'month'>('category');
  const [budgetPeriod, setBudgetPeriod] = useState<'this_month' | 'six_months' | 'one_year' | 'ytd'>('six_months');
  const [showBudgetEditModal, setShowBudgetEditModal] = useState(false);
  const [editingBudgetCategory, setEditingBudgetCategory] = useState<string>('');
  const [editingBudgetMonth, setEditingBudgetMonth] = useState<string>('');
  const [editingBudgetAmount, setEditingBudgetAmount] = useState<string>('');

  // File input refs
  const fileInputRef = useRef<HTMLInputElement>(null);
  const budgetFileInputRef = useRef<HTMLInputElement>(null);

  // Load categories on mount
  useEffect(() => {
    loadCategories();
  }, []);

  // Load data when month or trend period changes
  useEffect(() => {
    loadData();
  }, [selectedMonth, trendPeriod]);

  // Load budget vs actual data when period or view changes
  useEffect(() => {
    loadBudgetVsActualData();
  }, [selectedMonth, budgetPeriod, budgetViewBy]);

  const loadCategories = async () => {
    try {
      const response = await apiClient.get('/budget/categories');
      setIncomeCategories(response.data.income_categories);
      setExpenseCategories(response.data.expense_categories);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  // Calculate trend months based on selected period
  const getTrendMonths = (): number => {
    const [, selectedMonthNum] = selectedMonth.split('-').map(Number);

    switch (trendPeriod) {
      case 'this_month':
        return 1;
      case 'six_months':
        return 6;
      case 'one_year':
        return 12;
      case 'ytd':
        // Calculate months from January of selected year to selected month
        return selectedMonthNum;
      default:
        return 6;
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const trendMonths = getTrendMonths();
      const [transactionsRes, summaryRes, trendsRes] = await Promise.all([
        apiClient.get('/budget/transactions', { params: { month: selectedMonth } }),
        apiClient.get('/budget/summary', { params: { month: selectedMonth } }),
        apiClient.get('/budget/trends', { params: { months: trendMonths, end_month: selectedMonth } }),
      ]);

      setTransactions(transactionsRes.data.transactions);
      setSummary(summaryRes.data);
      setTrends(trendsRes.data.trends);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load budget data');
    } finally {
      setLoading(false);
    }
  };

  const loadInsights = async () => {
    setInsightsLoading(true);
    try {
      const response = await apiClient.post('/budget/insights', { month: selectedMonth });
      setInsights(response.data);
    } catch (err: any) {
      console.error('Failed to load insights:', err);
    } finally {
      setInsightsLoading(false);
    }
  };

  // Calculate budget period months
  const getBudgetPeriodMonths = (): number => {
    const [, selectedMonthNum] = selectedMonth.split('-').map(Number);

    switch (budgetPeriod) {
      case 'this_month':
        return 1;
      case 'six_months':
        return 6;
      case 'one_year':
        return 12;
      case 'ytd':
        return selectedMonthNum;
      default:
        return 6;
    }
  };

  const loadBudgetVsActualData = async () => {
    try {
      const months = getBudgetPeriodMonths();
      const response = await apiClient.get('/budget/budget-vs-actual', {
        params: { months, end_month: selectedMonth, view_by: budgetViewBy }
      });
      setBudgetVsActualData(response.data);
    } catch (err: any) {
      console.error('Failed to load budget vs actual data:', err);
    }
  };

  const handleImportBudgetCSV = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    const isPdf = file.name.toLowerCase().endsWith('.pdf');
    setLoadingOverlay({
      isVisible: true,
      message: t('budget:importing', 'Importing Budget Data...'),
      subMessage: isPdf
        ? t('budget:importingPdfHint', 'Processing PDF (this may take a moment for scanned documents)')
        : t('budget:importingCsvHint', 'Analyzing and extracting budget information')
    });

    try {
      const response = await apiClient.post('/budget/import/budget-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // Auto-navigate to a month with imported budget data
      const monthsFound = response.data.months_found;
      if (monthsFound && monthsFound.length > 0) {
        const mostRecentMonth = monthsFound[monthsFound.length - 1];
        // Setting selectedMonth triggers useEffect which reloads data
        setSelectedMonth(mostRecentMonth);
      } else {
        // No month info - reload current month's data
        loadBudgetVsActualData();
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to import budget file');
    } finally {
      setLoadingOverlay({ isVisible: false, message: '', subMessage: undefined });
      if (budgetFileInputRef.current) {
        budgetFileInputRef.current.value = '';
      }
    }
  };

  const handleSaveBudget = async () => {
    if (!editingBudgetCategory || !editingBudgetMonth || !editingBudgetAmount) {
      return;
    }

    try {
      await apiClient.put('/budget/category-budgets', {
        category_id: editingBudgetCategory,
        month: editingBudgetMonth,
        amount: parseFloat(editingBudgetAmount)
      });
      setShowBudgetEditModal(false);
      setEditingBudgetCategory('');
      setEditingBudgetMonth('');
      setEditingBudgetAmount('');
      loadBudgetVsActualData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save budget');
    }
  };

  const openBudgetEditModal = (categoryId: string = '', _categoryName: string = '') => {
    setEditingBudgetCategory(categoryId);
    setEditingBudgetMonth(selectedMonth);
    setEditingBudgetAmount('');
    setShowBudgetEditModal(true);
  };

  const handleAddTransaction = async () => {
    if (!transactionForm.category_id || !transactionForm.amount) {
      return;
    }

    try {
      const payload = {
        ...transactionForm,
        amount: parseFloat(transactionForm.amount),
        recurrence_period: transactionForm.is_recurring ? transactionForm.recurrence_period : null,
      };

      if (editingTransaction) {
        await apiClient.put(`/budget/transactions/${editingTransaction.id}`, payload);
      } else {
        await apiClient.post('/budget/transactions', payload);
      }

      setShowAddModal(false);
      setEditingTransaction(null);
      resetForm();
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save transaction');
    }
  };

  const handleDeleteTransaction = async (id: string) => {
    if (!window.confirm(t('budget:confirmDelete', 'Are you sure you want to delete this transaction?'))) {
      return;
    }

    try {
      await apiClient.delete(`/budget/transactions/${id}`);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete transaction');
    }
  };

  const handleEditTransaction = (transaction: Transaction) => {
    setEditingTransaction(transaction);
    setTransactionForm({
      type: transaction.type as 'income' | 'expense',
      category_id: transaction.category_id,
      amount: transaction.amount.toString(),
      description: transaction.description || '',
      date: transaction.date,
      is_recurring: transaction.is_recurring,
      recurrence_period: transaction.recurrence_period || '',
      tags: transaction.tags,
    });
    setShowAddModal(true);
  };

  const resetForm = () => {
    setTransactionForm({
      type: 'expense',
      category_id: '',
      amount: '',
      description: '',
      date: new Date().toISOString().slice(0, 10),
      is_recurring: false,
      recurrence_period: '',
      tags: [],
    });
  };

  const handleImportQuicken = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    const isPdf = file.name.toLowerCase().endsWith('.pdf');
    setLoadingOverlay({
      isVisible: true,
      message: t('budget:importing', 'Importing Budget Data...'),
      subMessage: isPdf
        ? t('budget:importingPdfHint', 'Processing PDF (this may take a moment for scanned documents)')
        : t('budget:importingCsvHint', 'Analyzing and extracting budget information')
    });

    try {
      const response = await apiClient.post('/budget/import/quicken', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // Auto-navigate to the most recent month with imported data
      const dateRange = response.data.date_range;
      if (dateRange?.most_recent_month) {
        // Setting selectedMonth triggers useEffect which reloads data
        setSelectedMonth(dateRange.most_recent_month);
      } else {
        // No date range info - reload current month's data
        loadData();
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to import Quicken file');
    } finally {
      setLoadingOverlay({ isVisible: false, message: '', subMessage: undefined });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await apiClient.get('/budget/export/csv', {
        params: { month: selectedMonth },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `auxilio_budget_${selectedMonth}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      setError('Failed to export CSV');
    }
  };

  // Generate export content for Word/PDF/Text
  const generateExportContent = () => {
    const totalIncome = transactions.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0);
    const totalExpenses = transactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + t.amount, 0);

    let content = `Budget Summary - ${selectedMonth}\n`;
    content += `Generated: ${new Date().toLocaleString()}\n\n`;
    content += `=== SUMMARY ===\n`;
    content += `Total Income: ${formatCurrency(totalIncome)}\n`;
    content += `Total Expenses: ${formatCurrency(totalExpenses)}\n`;
    content += `Net: ${formatCurrency(totalIncome - totalExpenses)}\n\n`;

    content += `=== TRANSACTIONS ===\n`;
    transactions.forEach(t => {
      content += `${t.date} | ${t.type.toUpperCase()} | ${t.category_id} | ${formatCurrency(t.amount)}`;
      if (t.description) content += ` | ${t.description}`;
      content += `\n`;
    });

    content += `\n---\nGenerated by Auxilio Finance\n`;
    return content;
  };

  // Handle export to Word/PDF/Text
  const handleExport = async (format: 'word' | 'pdf' | 'text' | 'csv') => {
    if (format === 'csv') {
      handleExportCSV();
      return;
    }

    const content = generateExportContent();
    const title = `Budget Summary - ${selectedMonth}`;

    const formatMap: Record<string, string> = {
      'word': 'docx',
      'pdf': 'pdf',
      'text': 'txt',
    };

    try {
      const response = await apiClient.post('/export/document', {
        content: content,
        title: title,
        format: formatMap[format],
      }, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title}.${formatMap[format]}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export document:', err);
      setError('Failed to export document');
    }
  };

  // Handle print
  const handlePrint = () => {
    const content = generateExportContent();
    const title = `Budget Summary - ${selectedMonth}`;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>${title}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 40px; line-height: 1.6; }
              h1 { color: #d97706; border-bottom: 2px solid #d97706; padding-bottom: 10px; }
              pre { white-space: pre-wrap; font-family: inherit; }
            </style>
          </head>
          <body>
            <pre>${content}</pre>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handleClearAllData = async () => {
    if (!window.confirm(t('budget:confirmClearAll', 'Are you sure you want to clear ALL budget data? This cannot be undone.'))) {
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.delete('/budget/clear-all');
      alert(response.data.message);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to clear data');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryName = (categoryId: string, type: string): string => {
    const categories = type === 'income' ? incomeCategories : expenseCategories;
    const category = categories.find((c) => c.id === categoryId);
    return category?.name || categoryId;
  };

  const filteredTransactions = transactions.filter((t) => {
    if (filterType !== 'all' && t.type !== filterType) return false;
    if (filterCategory && t.category_id !== filterCategory) return false;
    return true;
  });

  // Prepare chart data
  const expenseChartData = Object.entries(summary?.expense_by_category || {}).map(([id, amount]) => ({
    name: getCategoryName(id, 'expense'),
    value: amount,
  }));

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Loading Overlay */}
      <LoadingOverlay
        isVisible={loadingOverlay.isVisible}
        message={loadingOverlay.message}
        subMessage={loadingOverlay.subMessage}
      />

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <BanknotesIcon className="h-8 w-8 text-amber-500" />
            {t('budget:title', 'Budget Tracker')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t('budget:subtitle', 'Track your income and expenses with AI-powered insights')}
          </p>
        </div>

        <div className="flex items-center gap-3 mt-4 md:mt-0">
          {/* Month Selector */}
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />

          {/* Import/Export/Clear */}
          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".qif,.QIF,.qfx,.QFX,.ofx,.OFX,.csv,.CSV,.pdf,.PDF"
              onChange={handleImportQuicken}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-amber-500 dark:hover:text-amber-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title={t('budget:importQuicken', 'Import from Quicken (QIF/QFX/OFX/CSV/PDF)')}
            >
              <ArrowUpTrayIcon className="h-5 w-5" />
            </button>
            <ExportDropdownMenu
              onExport={handleExport}
              onPrint={handlePrint}
              onExportCSV={handleExportCSV}
              showCSV={true}
              showPrint={true}
            />
            <button
              onClick={handleClearAllData}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title={t('budget:clearAllData', 'Clear All Data (for testing)')}
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Add Transaction Button */}
          <Button onClick={() => { resetForm(); setShowAddModal(true); }}>
            <PlusIcon className="h-5 w-5 mr-1" />
            {t('budget:addTransaction', 'Add Transaction')}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
        {(['overview', 'transactions', 'insights'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => {
              setActiveTab(tab);
              if (tab === 'insights' && !insights) {
                loadInsights();
              }
            }}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-amber-500 text-amber-600 dark:text-amber-400'
                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            {tab === 'overview' && t('budget:tabs.overview', 'Overview')}
            {tab === 'transactions' && t('budget:tabs.transactions', 'Transactions')}
            {tab === 'insights' && (
              <span className="flex items-center gap-1">
                <SparklesIcon className="h-4 w-4" />
                {t('budget:tabs.insights', 'AI Insights')}
              </span>
            )}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
        </div>
      ) : (
        <>
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400 text-sm">
                      {t('budget:totalIncome', 'Total Income')}
                    </span>
                    <ArrowUpIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
                  </div>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1 whitespace-nowrap overflow-hidden text-ellipsis">
                    {formatCurrency(summary?.total_income || 0)}
                  </p>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400 text-sm">
                      {t('budget:totalExpenses', 'Total Expenses')}
                    </span>
                    <ArrowDownIcon className="h-5 w-5 text-red-500 flex-shrink-0" />
                  </div>
                  <p className="text-2xl font-bold text-red-600 dark:text-red-400 mt-1 whitespace-nowrap overflow-hidden text-ellipsis">
                    {formatCurrency(summary?.total_expenses || 0)}
                  </p>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400 text-sm">
                      {t('budget:netIncome', 'Net Income')}
                    </span>
                    <BanknotesIcon className="h-5 w-5 text-amber-500 flex-shrink-0" />
                  </div>
                  <p className={`text-2xl font-bold mt-1 whitespace-nowrap overflow-hidden text-ellipsis ${
                    (summary?.net || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                  }`}>
                    {formatCurrency(summary?.net || 0)}
                  </p>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400 text-sm">
                      {t('budget:savingsRate', 'Savings Rate')}
                    </span>
                    <ChartBarIcon className="h-5 w-5 text-blue-500" />
                  </div>
                  <p className={`text-2xl font-bold mt-1 ${
                    (summary?.savings_rate || 0) >= 20 ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'
                  }`}>
                    {(summary?.savings_rate || 0).toFixed(1)}%
                  </p>
                </div>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Expense Breakdown Pie Chart with Category List */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {t('budget:expenseBreakdown', 'Expense Breakdown')}
                    </h3>
                    <select
                      value={expenseSort}
                      onChange={(e) => setExpenseSort(e.target.value as typeof expenseSort)}
                      className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="amount_desc">{t('budget:sortAmountDesc', 'Amount (High to Low)')}</option>
                      <option value="amount_asc">{t('budget:sortAmountAsc', 'Amount (Low to High)')}</option>
                      <option value="percent_desc">{t('budget:sortPercentDesc', '% (High to Low)')}</option>
                      <option value="percent_asc">{t('budget:sortPercentAsc', '% (Low to High)')}</option>
                    </select>
                  </div>
                  {expenseChartData.length > 0 ? (
                    <div className="flex gap-4">
                      {/* Pie Chart - smaller */}
                      <div className="flex-shrink-0" style={{ width: '180px', height: '180px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={expenseChartData}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              outerRadius={70}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {expenseChartData.map((_, index) => (
                                <Cell key={`cell-${index}`} fill={EXPENSE_COLORS[index % EXPENSE_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value: number) => formatCurrency(value)} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      {/* Category List - scrollable */}
                      <div className="flex-1 overflow-y-auto max-h-64">
                        <div className="space-y-2">
                          {(() => {
                            const total = expenseChartData.reduce((sum, i) => sum + i.value, 0);
                            const dataWithPercent = expenseChartData.map((item) => ({
                              ...item,
                              percent: total > 0 ? (item.value / total) * 100 : 0,
                            }));
                            // Sort based on selected option
                            const sortedData = [...dataWithPercent].sort((a, b) => {
                              switch (expenseSort) {
                                case 'amount_desc': return b.value - a.value;
                                case 'amount_asc': return a.value - b.value;
                                case 'percent_desc': return b.percent - a.percent;
                                case 'percent_asc': return a.percent - b.percent;
                                default: return b.value - a.value;
                              }
                            });
                            return sortedData.map((item, index) => {
                              const percent = item.percent;
                              return (
                                <div key={item.name} className="flex items-center gap-2">
                                  <div
                                    className="w-3 h-3 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: EXPENSE_COLORS[index % EXPENSE_COLORS.length] }}
                                  />
                                  <span className="flex-1 text-sm text-gray-700 dark:text-gray-300 min-w-0 break-words">
                                    {item.name}
                                  </span>
                                  <span className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0 whitespace-nowrap text-right"
                                        style={{ minWidth: '3rem' }}>
                                    {percent.toFixed(1)}%
                                  </span>
                                  <span className="text-sm font-medium text-gray-900 dark:text-white flex-shrink-0 whitespace-nowrap text-right"
                                        style={{ minWidth: '5rem' }}>
                                    {formatCurrency(item.value)}
                                  </span>
                                </div>
                              );
                            });
                          })()}
                        </div>
                        {/* Total */}
                        <div className="border-t border-gray-200 dark:border-gray-600 mt-3 pt-3 flex items-center gap-2">
                          <div className="w-3 h-3 flex-shrink-0" />
                          <span className="flex-1 text-sm font-semibold text-gray-900 dark:text-white min-w-0">
                            {t('budget:total', 'Total')}
                          </span>
                          <span className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0 whitespace-nowrap text-right"
                                style={{ minWidth: '3rem' }}>
                            100%
                          </span>
                          <span className="text-sm font-bold text-gray-900 dark:text-white flex-shrink-0 whitespace-nowrap text-right"
                                style={{ minWidth: '5rem' }}>
                            {formatCurrency(summary?.total_expenses || 0)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-48 text-gray-500 dark:text-gray-400">
                      {t('budget:noExpenses', 'No expenses recorded for this month')}
                    </div>
                  )}
                </div>

                {/* Income vs Expenses Trend */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {t('budget:incomeVsExpenses', 'Income vs Expenses Trend')}
                    </h3>
                    <select
                      value={trendPeriod}
                      onChange={(e) => setTrendPeriod(e.target.value as typeof trendPeriod)}
                      className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="this_month">{t('budget:thisMonth', 'This Month')}</option>
                      <option value="six_months">{t('budget:lastSixMonths', 'Last 6 Months')}</option>
                      <option value="one_year">{t('budget:lastYear', 'Last Year')}</option>
                      <option value="ytd">{t('budget:yearToDate', 'Year to Date')}</option>
                    </select>
                  </div>
                  {trends.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={trends}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="month"
                          tickFormatter={(value) => {
                            // Format YYYY-MM as Mon 'YY (e.g., "Dec '25")
                            if (value && value.includes('-')) {
                              const [year, month] = value.split('-');
                              const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                                                  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                              return `${monthNames[parseInt(month, 10) - 1]} '${year.slice(2)}`;
                            }
                            return value;
                          }}
                        />
                        <YAxis
                          tickFormatter={(value) => {
                            if (value === 0) return '$0';
                            if (value >= 1000) return `$${(value / 1000).toFixed(0)}k`;
                            return `$${value}`;
                          }}
                          domain={[0, 'auto']}
                        />
                        <Tooltip formatter={(value: number) => formatCurrency(value)} />
                        <Legend />
                        <Bar dataKey="income" name={t('budget:income', 'Income')} fill="#10b981" />
                        <Bar dataKey="expenses" name={t('budget:expenses', 'Expenses')} fill="#ef4444" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
                      {t('budget:noTrends', 'Not enough data to show trends')}
                    </div>
                  )}
                </div>
              </div>

              {/* Expense and Income against Budget Panel */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t('budget:budgetVsActual', 'Expense and Income against Budget')}
                  </h3>
                  <div className="flex flex-wrap items-center gap-3">
                    {/* Import Budget Button */}
                    <input
                      ref={budgetFileInputRef}
                      type="file"
                      accept=".csv,.CSV,.pdf,.PDF"
                      onChange={handleImportBudgetCSV}
                      className="hidden"
                    />
                    <button
                      onClick={() => budgetFileInputRef.current?.click()}
                      className="px-3 py-1.5 text-sm bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-lg hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors"
                    >
                      <ArrowUpTrayIcon className="h-4 w-4 inline mr-1" />
                      {t('budget:importBudget', 'Import Budget')}
                    </button>
                    {/* View By Selector */}
                    <select
                      value={budgetViewBy}
                      onChange={(e) => setBudgetViewBy(e.target.value as 'category' | 'month')}
                      className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="category">{t('budget:viewByCategory', 'View by Category')}</option>
                      <option value="month">{t('budget:viewByMonth', 'View by Month')}</option>
                    </select>
                    {/* Time Period Selector */}
                    <select
                      value={budgetPeriod}
                      onChange={(e) => setBudgetPeriod(e.target.value as typeof budgetPeriod)}
                      className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="this_month">{t('budget:thisMonth', 'This Month')}</option>
                      <option value="six_months">{t('budget:lastSixMonths', 'Last 6 Months')}</option>
                      <option value="one_year">{t('budget:lastYear', 'Last Year')}</option>
                      <option value="ytd">{t('budget:yearToDate', 'Year to Date')}</option>
                    </select>
                    {/* Edit Budget Button */}
                    <button
                      onClick={() => openBudgetEditModal('', '')}
                      className="px-3 py-1.5 text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                    >
                      <PencilIcon className="h-4 w-4 inline mr-1" />
                      {t('budget:editBudget', 'Edit Budget')}
                    </button>
                  </div>
                </div>

                {budgetVsActualData ? (
                  <div className="overflow-x-auto">
                    {budgetViewBy === 'month' ? (
                      /* View by Month - show monthly totals */
                      <ResponsiveContainer width="100%" height={350}>
                        <BarChart data={budgetVsActualData.chart_data || []}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="label"
                            tickFormatter={(value) => {
                              if (value && value.includes('-')) {
                                const [year, month] = value.split('-');
                                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                                                    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                return `${monthNames[parseInt(month, 10) - 1]} '${year.slice(2)}`;
                              }
                              return value;
                            }}
                          />
                          <YAxis
                            tickFormatter={(value) => {
                              if (value === 0) return '$0';
                              if (value >= 1000) return `$${(value / 1000).toFixed(0)}k`;
                              return `$${value}`;
                            }}
                          />
                          <Tooltip formatter={(value: number) => formatCurrency(value)} />
                          <Legend />
                          <Bar dataKey="income_budget" name={t('budget:incomeBudget', 'Income Budget')} fill="#86efac" />
                          <Bar dataKey="income_actual" name={t('budget:incomeActual', 'Income Actual')} fill="#10b981" />
                          <Bar dataKey="expense_budget" name={t('budget:expenseBudget', 'Expense Budget')} fill="#fca5a5" />
                          <Bar dataKey="expense_actual" name={t('budget:expenseActual', 'Expense Actual')} fill="#ef4444" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      /* View by Category - show category totals */
                      <div className="space-y-6">
                        {/* Expense Categories */}
                        {budgetVsActualData.expense_data && budgetVsActualData.expense_data.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                              {t('budget:expenseCategories', 'Expense Categories')}
                            </h4>
                            <div className="overflow-x-auto pb-2" style={{ maxWidth: '100%' }}>
                              <div style={{ minWidth: `${Math.max(600, budgetVsActualData.expense_data.length * 100)}px` }}>
                                <ResponsiveContainer width="100%" height={280}>
                                  <BarChart data={budgetVsActualData.expense_data} layout="horizontal">
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                      dataKey="label"
                                      angle={-45}
                                      textAnchor="end"
                                      height={80}
                                      interval={0}
                                      tick={{ fontSize: 11 }}
                                    />
                                    <YAxis
                                      tickFormatter={(value) => {
                                        if (value === 0) return '$0';
                                        if (value >= 1000) return `$${(value / 1000).toFixed(0)}k`;
                                        return `$${value}`;
                                      }}
                                    />
                                    <Tooltip formatter={(value: number) => formatCurrency(value)} />
                                    <Legend />
                                    <Bar dataKey="budget" name={t('budget:budgeted', 'Budgeted')} fill="#fca5a5" />
                                    <Bar dataKey="actual" name={t('budget:actual', 'Actual')} fill="#ef4444" />
                                  </BarChart>
                                </ResponsiveContainer>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Income Categories */}
                        {budgetVsActualData.income_data && budgetVsActualData.income_data.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                              {t('budget:incomeCategories', 'Income Categories')}
                            </h4>
                            <div className="overflow-x-auto pb-2" style={{ maxWidth: '100%' }}>
                              <div style={{ minWidth: `${Math.max(400, budgetVsActualData.income_data.length * 100)}px` }}>
                                <ResponsiveContainer width="100%" height={220}>
                                  <BarChart data={budgetVsActualData.income_data} layout="horizontal">
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                      dataKey="label"
                                      angle={-45}
                                      textAnchor="end"
                                      height={80}
                                      interval={0}
                                      tick={{ fontSize: 11 }}
                                    />
                                    <YAxis
                                      tickFormatter={(value) => {
                                        if (value === 0) return '$0';
                                        if (value >= 1000) return `$${(value / 1000).toFixed(0)}k`;
                                        return `$${value}`;
                                      }}
                                    />
                                    <Tooltip formatter={(value: number) => formatCurrency(value)} />
                                    <Legend />
                                    <Bar dataKey="budget" name={t('budget:budgeted', 'Budgeted')} fill="#86efac" />
                                    <Bar dataKey="actual" name={t('budget:actual', 'Actual')} fill="#10b981" />
                                  </BarChart>
                                </ResponsiveContainer>
                              </div>
                            </div>
                          </div>
                        )}

                        {(!budgetVsActualData.expense_data || budgetVsActualData.expense_data.length === 0) &&
                         (!budgetVsActualData.income_data || budgetVsActualData.income_data.length === 0) && (
                          <div className="flex items-center justify-center h-48 text-gray-500 dark:text-gray-400">
                            {t('budget:noBudgetData', 'No budget or transaction data available. Import a budget or add transactions.')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-48 text-gray-500 dark:text-gray-400">
                    {t('budget:loadingBudgetData', 'Loading budget data...')}
                  </div>
                )}
              </div>

              {/* Import from Quicken Info */}
              <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border border-amber-200 dark:border-amber-800">
                <h3 className="font-semibold text-amber-800 dark:text-amber-200 mb-2">
                  {t('budget:quickenIntegration', 'Import from Quicken')}
                </h3>
                <p className="text-amber-700 dark:text-amber-300 text-sm">
                  {t('budget:quickenInfo', 'Import transactions from Quicken using QIF files. Categories are automatically mapped for seamless integration. Use the Export menu above for CSV, Word, PDF, or Text exports.')}
                </p>
                <div className="flex gap-4 mt-3">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="text-amber-600 dark:text-amber-400 text-sm font-medium hover:underline"
                  >
                    {t('budget:importFromQuicken', 'Import from Quicken')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Transactions Tab */}
          {activeTab === 'transactions' && (
            <div className="space-y-4">
              {/* Filters */}
              <div className="flex flex-wrap gap-4 items-center bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                <FunnelIcon className="h-5 w-5 text-gray-500" />
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value as any)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="all">{t('budget:allTypes', 'All Types')}</option>
                  <option value="income">{t('budget:income', 'Income')}</option>
                  <option value="expense">{t('budget:expense', 'Expense')}</option>
                </select>

                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">{t('budget:allCategories', 'All Categories')}</option>
                  <optgroup label={t('budget:income', 'Income')}>
                    {incomeCategories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </optgroup>
                  <optgroup label={t('budget:expenses', 'Expenses')}>
                    {expenseCategories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </optgroup>
                </select>

                <span className="text-sm text-gray-500 dark:text-gray-400 ml-auto">
                  {filteredTransactions.length} {t('budget:transactions', 'transactions')}
                </span>
              </div>

              {/* Transaction List */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                {filteredTransactions.length > 0 ? (
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredTransactions.map((transaction) => (
                      <div
                        key={transaction.id}
                        className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors flex items-center justify-between"
                      >
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-lg ${
                            transaction.type === 'income'
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                              : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                          }`}>
                            {transaction.type === 'income' ? (
                              <ArrowUpIcon className="h-5 w-5" />
                            ) : (
                              <ArrowDownIcon className="h-5 w-5" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {getCategoryName(transaction.category_id, transaction.type)}
                            </p>
                            {transaction.description && (
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {transaction.description}
                              </p>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              <CalendarIcon className="h-4 w-4 text-gray-400" />
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {new Date(transaction.date).toLocaleDateString()}
                              </span>
                              {transaction.tags.length > 0 && (
                                <>
                                  <TagIcon className="h-4 w-4 text-gray-400 ml-2" />
                                  {transaction.tags.map((tag) => (
                                    <span
                                      key={tag}
                                      className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded"
                                    >
                                      {tag}
                                    </span>
                                  ))}
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-4 flex-shrink-0">
                          <span className={`text-lg font-semibold whitespace-nowrap ${
                            transaction.type === 'income'
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-red-600 dark:text-red-400'
                          }`}>
                            {transaction.type === 'income' ? '+' : '-'}
                            {formatCurrency(transaction.amount)}
                          </span>
                          <div className="flex gap-1 flex-shrink-0">
                            <button
                              onClick={() => handleEditTransaction(transaction)}
                              className="p-2 text-gray-500 hover:text-amber-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteTransaction(transaction.id)}
                              className="p-2 text-gray-500 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-12 text-center text-gray-500 dark:text-gray-400">
                    <BanknotesIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>{t('budget:noTransactions', 'No transactions found for this month')}</p>
                    <Button onClick={() => setShowAddModal(true)} className="mt-4">
                      <PlusIcon className="h-5 w-5 mr-1" />
                      {t('budget:addFirst', 'Add your first transaction')}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* AI Insights Tab */}
          {activeTab === 'insights' && (
            <div className="space-y-6">
              {insightsLoading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400">
                      {t('budget:analyzingSpending', 'Analyzing your spending patterns...')}
                    </p>
                  </div>
                </div>
              ) : insights ? (
                <>
                  {/* Insights */}
                  {insights.insights && insights.insights.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <SparklesIcon className="h-5 w-5 text-amber-500" />
                        {t('budget:keyInsights', 'Key Insights')}
                      </h3>
                      <ul className="space-y-2">
                        {insights.insights.map((insight, index) => (
                          <li key={index} className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                            <span className="text-amber-500 mt-1">•</span>
                            {insight}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Recommendations */}
                  {insights.recommendations && insights.recommendations.length > 0 && (
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-6 border border-green-200 dark:border-green-800">
                      <h3 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-4">
                        {t('budget:recommendations', 'Recommendations')}
                      </h3>
                      <ul className="space-y-2">
                        {insights.recommendations.map((rec, index) => (
                          <li key={index} className="flex items-start gap-2 text-green-700 dark:text-green-300">
                            <CheckIcon className="h-5 w-5 mt-0.5 flex-shrink-0" />
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Areas of Concern */}
                  {insights.areas_of_concern && insights.areas_of_concern.length > 0 && (
                    <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-800">
                      <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-4">
                        {t('budget:areasOfConcern', 'Areas of Concern')}
                      </h3>
                      <ul className="space-y-2">
                        {insights.areas_of_concern.map((concern, index) => (
                          <li key={index} className="flex items-start gap-2 text-red-700 dark:text-red-300">
                            <span className="text-red-500 mt-1">!</span>
                            {concern}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Savings Opportunities */}
                  {insights.savings_opportunities && insights.savings_opportunities.length > 0 && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 border border-blue-200 dark:border-blue-800">
                      <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-4">
                        {t('budget:savingsOpportunities', 'Savings Opportunities')}
                      </h3>
                      <ul className="space-y-2">
                        {insights.savings_opportunities.map((opp, index) => (
                          <li key={index} className="flex items-start gap-2 text-blue-700 dark:text-blue-300">
                            <BanknotesIcon className="h-5 w-5 mt-0.5 flex-shrink-0" />
                            {opp}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <button
                    onClick={loadInsights}
                    className="text-amber-600 dark:text-amber-400 text-sm font-medium hover:underline"
                  >
                    {t('budget:refreshInsights', 'Refresh AI Insights')}
                  </button>
                </>
              ) : (
                <div className="text-center py-12">
                  <SparklesIcon className="h-12 w-12 mx-auto mb-4 text-amber-500" />
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    {t('budget:noInsightsYet', 'Click below to get AI-powered insights on your spending')}
                  </p>
                  <Button onClick={loadInsights}>
                    <SparklesIcon className="h-5 w-5 mr-1" />
                    {t('budget:getInsights', 'Get AI Insights')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Add/Edit Transaction Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingTransaction
                  ? t('budget:editTransaction', 'Edit Transaction')
                  : t('budget:addTransaction', 'Add Transaction')}
              </h2>
              <button
                onClick={() => { setShowAddModal(false); setEditingTransaction(null); }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Type Selection */}
              <div className="flex gap-2">
                <button
                  onClick={() => setTransactionForm({ ...transactionForm, type: 'income', category_id: '' })}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                    transactionForm.type === 'income'
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-2 border-green-500'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  }`}
                >
                  <ArrowUpIcon className="h-5 w-5 inline mr-1" />
                  {t('budget:income', 'Income')}
                </button>
                <button
                  onClick={() => setTransactionForm({ ...transactionForm, type: 'expense', category_id: '' })}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                    transactionForm.type === 'expense'
                      ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-2 border-red-500'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  }`}
                >
                  <ArrowDownIcon className="h-5 w-5 inline mr-1" />
                  {t('budget:expense', 'Expense')}
                </button>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('budget:category', 'Category')} *
                </label>
                <select
                  value={transactionForm.category_id}
                  onChange={(e) => setTransactionForm({ ...transactionForm, category_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                >
                  <option value="">{t('budget:selectCategory', 'Select a category')}</option>
                  {(transactionForm.type === 'income' ? incomeCategories : expenseCategories).map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('budget:amount', 'Amount')} *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500 dark:text-gray-400">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={transactionForm.amount}
                    onChange={(e) => setTransactionForm({ ...transactionForm, amount: e.target.value })}
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('budget:date', 'Date')}
                </label>
                <input
                  type="date"
                  value={transactionForm.date}
                  onChange={(e) => setTransactionForm({ ...transactionForm, date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  style={dateInputStyle}
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('budget:description', 'Description')}
                </label>
                <input
                  type="text"
                  value={transactionForm.description}
                  onChange={(e) => setTransactionForm({ ...transactionForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder={t('budget:descriptionPlaceholder', 'e.g., Grocery shopping at Costco')}
                />
              </div>

              {/* Recurring */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="recurring"
                  checked={transactionForm.is_recurring}
                  onChange={(e) => setTransactionForm({ ...transactionForm, is_recurring: e.target.checked })}
                  className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                />
                <label htmlFor="recurring" className="text-sm text-gray-700 dark:text-gray-300">
                  {t('budget:recurring', 'This is a recurring transaction')}
                </label>
              </div>

              {transactionForm.is_recurring && (
                <select
                  value={transactionForm.recurrence_period}
                  onChange={(e) => setTransactionForm({ ...transactionForm, recurrence_period: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">{t('budget:selectFrequency', 'Select frequency')}</option>
                  <option value="weekly">{t('budget:weekly', 'Weekly')}</option>
                  <option value="biweekly">{t('budget:biweekly', 'Bi-weekly')}</option>
                  <option value="monthly">{t('budget:monthly', 'Monthly')}</option>
                  <option value="yearly">{t('budget:yearly', 'Yearly')}</option>
                </select>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="secondary"
                onClick={() => { setShowAddModal(false); setEditingTransaction(null); }}
                fullWidth
              >
                {t('common:buttons.cancel', 'Cancel')}
              </Button>
              <Button
                onClick={handleAddTransaction}
                fullWidth
                disabled={!transactionForm.category_id || !transactionForm.amount}
              >
                {editingTransaction
                  ? t('common:buttons.save', 'Save')
                  : t('budget:addTransaction', 'Add Transaction')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Budget Edit Modal */}
      {showBudgetEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('budget:editCategoryBudget', 'Edit Category Budget')}
              </h2>
              <button
                onClick={() => setShowBudgetEditModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Category Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('budget:category', 'Category')} *
                </label>
                <select
                  value={editingBudgetCategory}
                  onChange={(e) => setEditingBudgetCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                >
                  <option value="">{t('budget:selectCategory', 'Select a category')}</option>
                  <optgroup label={t('budget:income', 'Income')}>
                    {incomeCategories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </optgroup>
                  <optgroup label={t('budget:expenses', 'Expenses')}>
                    {expenseCategories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </optgroup>
                </select>
              </div>

              {/* Month Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('budget:month', 'Month')} *
                </label>
                <input
                  type="month"
                  value={editingBudgetMonth}
                  onChange={(e) => setEditingBudgetMonth(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                />
              </div>

              {/* Budget Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('budget:budgetAmount', 'Budget Amount')} *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500 dark:text-gray-400">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editingBudgetAmount}
                    onChange={(e) => setEditingBudgetAmount(e.target.value)}
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="secondary"
                onClick={() => setShowBudgetEditModal(false)}
                fullWidth
              >
                {t('common:buttons.cancel', 'Cancel')}
              </Button>
              <Button
                onClick={handleSaveBudget}
                fullWidth
                disabled={!editingBudgetCategory || !editingBudgetMonth || !editingBudgetAmount}
              >
                {t('common:buttons.save', 'Save Budget')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BudgetTracker;
